import axios from 'axios';
import { supabase, Profile, ScraperLog } from '../utils/supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
};

/** Estado de un proceso programado (GET /admin/cron/status). Los campos varían por proceso; todos son opcionales. */
export interface SchedulerStatus {
  isRunning?: boolean;
  enabled?: boolean;
  schedule?: string;
  timezone?: string;
  lastRun?: string | null;
  nextRun?: string | null;
  currentlyRunning?: boolean;
  lastResult?: unknown;
}

export interface CronStatus {
  timestamp: string;
  timezone: string;
  schedulers: { alert?: SchedulerStatus; scraper?: SchedulerStatus; notification?: SchedulerStatus };
}

/** `processing`: corridas de ingesta (con su costo en metadata.cost_usd); `scraper`: revisiones del sitio. */
export type LogKind = 'processing' | 'scraper';

/** Una solicitud de demo de la portada (tabla demo_requests, migración 133). */
export type DemoEstado = 'nueva' | 'contactada' | 'descartada';
export interface DemoRequest {
  id: string;
  nombre: string;
  correo: string;
  organizacion: string;
  estado: DemoEstado;
  nota: string | null;
  created_at: string;
  updated_at: string;
}

export const adminService = {
  async getUsers(): Promise<Profile[]> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/admin/users`, { headers });
    return response.data.data;
  },

  async updateUserRole(userId: string, role: 'admin' | 'user'): Promise<void> {
    const headers = await getAuthHeaders();
    await axios.put(`${API_URL}/admin/users/${userId}/role`, { role }, { headers });
  },

  async updateUserStatus(userId: string, status: 'approved' | 'rejected'): Promise<Profile> {
    const headers = await getAuthHeaders();
    const response = await axios.patch(`${API_URL}/admin/users/${userId}/status`, { status }, { headers });
    return response.data.data;
  },

  async getLogs(kind: LogKind, limit = 50): Promise<ScraperLog[]> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/admin/logs`, { params: { kind, limit }, headers });
    return response.data.data || [];
  },

  async getCronStatus(): Promise<CronStatus> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/admin/cron/status`, { headers });
    return response.data.data;
  },

  async getDemoRequests(): Promise<DemoRequest[]> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/admin/demo-requests`, { headers });
    return response.data.data || [];
  },

  async updateDemoRequest(id: string, cambios: { estado?: DemoEstado; nota?: string | null }): Promise<DemoRequest> {
    const headers = await getAuthHeaders();
    const response = await axios.patch(`${API_URL}/admin/demo-requests/${id}`, cambios, { headers });
    return response.data.data;
  },

  async deleteUser(userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    await axios.delete(`${API_URL}/admin/users/${userId}`, { headers });
  },
};

export default adminService;
